import { RING_REASONS, type RingReason, type SettingsAnswer, type WaitingItem, type WaitingOrder } from './ring-api'
import type { Lang } from './ring-i18n'

// R503 (c): the till ring's rules, pure, ported from HOP's features/ring/ring-rules.ts (R503 (b))
// with the same names. The till cannot import HOP's module: it reaches HOP's session module
// (lib/auth.ts) through HOP's ring-api, and the till builds from its own directory. The server
// says which orders wait and why (GET /admin/orders/waiting); the owner's settings say which
// screen rings for which reason, how often, how loud, when it is quiet and how long "Heard it"
// pauses (GET /admin/settings). The client holds no bound and no default of its own.

// The till's key in the owner's `ring.screens` map (R503 (a) registry).
export type RingScreen = 'till'

export interface ScreenRing {
  on: boolean
  reasons: RingReason[]
}

export interface QuietHours {
  start: string
  end: string
}

export interface RingSettings {
  intervalS: number
  loudness: number
  heardPauseS: number
  quietHours: QuietHours | null
  timeZone: string
  screens: Record<string, ScreenRing>
}

export interface RingRow {
  id: string
  shortCode: string | null
  // The person and the plate (Rook #67688) as the server sends them; null and [] when it does not.
  customerName: string | null
  items: WaitingItem[]
  reasons: { reason: RingReason; ageSeconds: number }[]
}

// The first one or two items: what the shop calls the order.
const PLATE_ITEMS = 2

function nameOf(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null
}

function plateOf(v: unknown): WaitingItem[] {
  if (!Array.isArray(v)) return []
  return v
    .filter((i): i is Record<string, unknown> => i !== null && typeof i === 'object')
    .map((i) => ({ nameEn: nameOf(i.nameEn), nameAr: nameOf(i.nameAr) }))
    .slice(0, PLATE_ITEMS)
}

/**
 * An item's name in a language, the Cockpit card's rule (QueueItem.tsx lineName): Arabic falls
 * back to English and English to Arabic; null when it has neither (the item is left out).
 */
export function itemName(item: WaitingItem, lang: Lang): string | null {
  return (lang === 'ar' ? item.nameAr ?? item.nameEn : item.nameEn ?? item.nameAr) ?? null
}

/** What leads the row: the customer's name and the plate in a language, or null (the code leads). */
export function whoPlate(row: RingRow, lang: Lang): { name: string; plate: string } | null {
  const names = row.items.map((i) => itemName(i, lang)).filter((n): n is string => n !== null)
  if (row.customerName === null || names.length === 0) return null
  return { name: row.customerName, plate: names.join(', ') }
}

// Beirut, as the registry's quiet-hours bounds name it; used only when the bounds carry no zone.
const QUIET_ZONE = 'Asia/Beirut'
const HHMM = /^([01][0-9]|2[0-3]):([0-5][0-9])$/

export class RingSettingsError extends Error {}

function isWhole(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v)
}

function isReason(v: unknown): v is RingReason {
  return typeof v === 'string' && (RING_REASONS as readonly string[]).includes(v)
}

function item(answer: SettingsAnswer, key: string) {
  const found = Array.isArray(answer?.settings) ? answer.settings.find((s) => s && s.key === key) : undefined
  if (!found) throw new RingSettingsError(`${key} is missing`)
  return found
}

function wholeSetting(answer: SettingsAnswer, key: string): number {
  const v = item(answer, key).value
  if (!isWhole(v)) throw new RingSettingsError(`${key} is not a whole number`)
  return v
}

/** Reads the ring's keys from GET /admin/settings; throws RingSettingsError when one is unreadable. */
export function parseRingSettings(answer: SettingsAnswer): RingSettings {
  const intervalS = wholeSetting(answer, 'ring.interval_s')
  if (intervalS <= 0) throw new RingSettingsError('ring.interval_s is not positive')
  const loudness = wholeSetting(answer, 'ring.loudness')
  const heardPauseS = wholeSetting(answer, 'ring.heard_pause_s')

  const qhItem = item(answer, 'ring.quiet_hours')
  const qh = qhItem.value as { start?: unknown; end?: unknown } | null
  let quietHours: QuietHours | null = null
  if (qh !== null) {
    if (typeof qh !== 'object' || typeof qh.start !== 'string' || typeof qh.end !== 'string'
      || !HHMM.test(qh.start) || !HHMM.test(qh.end)) {
      throw new RingSettingsError('ring.quiet_hours is unreadable')
    }
    quietHours = { start: qh.start, end: qh.end }
  }
  const zone = (qhItem.bounds as { timeZone?: unknown } | null)?.timeZone
  const timeZone = typeof zone === 'string' && zone !== '' ? zone : QUIET_ZONE

  const raw = item(answer, 'ring.screens').value as Record<string, unknown> | null
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) throw new RingSettingsError('ring.screens is unreadable')
  const screens: Record<string, ScreenRing> = {}
  for (const [name, cfg] of Object.entries(raw)) {
    const c = cfg as { on?: unknown; reasons?: unknown } | null
    if (!c || typeof c.on !== 'boolean' || !Array.isArray(c.reasons)) throw new RingSettingsError(`ring.screens.${name} is unreadable`)
    screens[name] = { on: c.on, reasons: c.reasons.filter(isReason) }
  }
  return { intervalS, loudness, heardPauseS, quietHours, timeZone, screens }
}

/** The orders this screen rings for: each order kept with only the reasons the screen's map names. */
export function ringRows(orders: WaitingOrder[], screen: ScreenRing | undefined): RingRow[] {
  if (!screen || !screen.on || !Array.isArray(orders)) return []
  const rows: RingRow[] = []
  for (const o of orders) {
    if (!o || typeof o.id !== 'string' || !Array.isArray(o.reasons)) continue
    const reasons = o.reasons
      .filter((r) => r && isReason(r.reason) && screen.reasons.includes(r.reason))
      .map((r) => ({ reason: r.reason as RingReason, ageSeconds: isWhole(r.ageSeconds) ? r.ageSeconds : 0 }))
    if (reasons.length === 0) continue
    rows.push({
      id: o.id,
      shortCode: typeof o.shortCode === 'string' ? o.shortCode : null,
      // As sent; whoPlate decides per language whether the person and the plate lead.
      customerName: nameOf(o.customerName),
      items: plateOf(o.items),
      reasons,
    })
  }
  return rows
}

function minutesOf(hhmm: string): number {
  const m = HHMM.exec(hhmm)
  return m ? Number(m[1]) * 60 + Number(m[2]) : -1
}

/** Minutes since midnight at `at` in the given time zone. */
export function zoneMinutes(at: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(at)
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0')
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0')
  return (hour % 24) * 60 + minute
}

/** Quiet from start (included) to end (excluded); a start after the end runs over midnight. */
export function inQuietHours(at: Date, quiet: QuietHours | null, timeZone: string): boolean {
  if (!quiet) return false
  const start = minutesOf(quiet.start)
  const end = minutesOf(quiet.end)
  if (start < 0 || end < 0 || start === end) return false
  const now = zoneMinutes(at, timeZone)
  return start < end ? now >= start && now < end : now >= start || now < end
}

// "Heard it" is this device's alone: one localStorage key, never sent anywhere.
export const HEARD_STORAGE_KEY = 'till.ring.heardUntil'

/** The time this device's pause ends (ms), or null when it is not paused at `nowMs`. */
export function heardUntil(nowMs: number): number | null {
  try {
    const raw = window.localStorage.getItem(HEARD_STORAGE_KEY)
    if (raw === null) return null
    const until = Number(raw)
    return Number.isFinite(until) && until > nowMs ? until : null
  } catch {
    return null
  }
}

/** Pauses this device for `pauseS` seconds from `nowMs`; returns the end (ms). */
export function markHeard(nowMs: number, pauseS: number): number {
  const until = nowMs + pauseS * 1000
  try {
    window.localStorage.setItem(HEARD_STORAGE_KEY, String(until))
  } catch {
    // Storage blocked (private mode): the pause holds for this screen only, in memory.
  }
  return until
}

/** Whether this poll makes a sound. */
export function soundsNow(input: { rows: RingRow[]; on: boolean; quiet: boolean; pausedUntil: number | null; loudness: number }): boolean {
  return input.on && input.rows.length > 0 && !input.quiet && input.pausedUntil === null && input.loudness > 0
}
